import {
    ChangeDetectionStrategy,
    Component,
    type ComponentRef,
    HostBinding,
    Input,
    type OnChanges,
    type SimpleChanges,
    ViewChild,
    ViewContainerRef,
    inject
} from '@angular/core';
import { DomSanitizer, type SafeStyle } from '@angular/platform-browser';
import type { DataSource } from '../../../data-viz/data-source.class';
import { DataVizWidgetAutoSpawnerComponent } from '../../../data-viz/data-viz-widgets/data-viz-widget-auto-spawner/data-viz-widget-auto-spawner';
import {
    type DataVizWidgetConfig,
    type DataVizWidgetSpawnerConfig,
    WidgetGhostType,
    WidgetShellType
} from '../../../data-viz/data-viz-widgets/data-viz-widget.interface';
import type { CustomInput } from '../../../interfaces/CustomInput.interface';
import type { RegularObject } from '../../../interfaces/utils.interface';
import type { LegacyFrontendWidgetConfig } from '../../../interfaces/Widget.interface';
import { LegacyWidgetCode } from '../../../providers/rights.constants';
import { WidgetClassStaticProvider } from '../../../providers/widget-class.static.provider';
import { WidgetSize } from '../../../providers/widgets.contants';
import type { AbstractWidgetClass } from '../AbstractWidget.class';

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./widget-spawner.ng-cli.scss'],
    selector: 'widget-spawner',
    templateUrl: 'widget-spawner.html',
    imports: [DataVizWidgetAutoSpawnerComponent]
})
export class WidgetSpawnerComponent implements OnChanges {
    private _sanitizer = inject(DomSanitizer);

    @ViewChild('spawn', { static: true, read: ViewContainerRef }) container!: ViewContainerRef;

    @HostBinding('style.minWidth') minWidth?: SafeStyle;
    @HostBinding('style.width') width?: SafeStyle;
    size!: WidgetSize;

    private _component: ComponentRef<AbstractWidgetClass<string, any>> | null = null;

    @Input() widget!: LegacyWidgetCode;
    @Input() config!: LegacyFrontendWidgetConfig<string, any> | DataVizWidgetSpawnerConfig;
    @Input() dataSources!: RegularObject<DataSource<any>>;
    @Input() customInputs!: CustomInput[];

    // to remove after configs update in database !
    ghostShellTempFixedConfig!: DataVizWidgetConfig;

    ngOnChanges(changes: SimpleChanges): void {
        if (this.widget === LegacyWidgetCode.WDGT_HISTOGRAM || (this.config && this.config.ghost)) {
            this.ghostShellTempFixedConfig = this.config.ghost
                ? this.config
                : {
                      customInputs: [],
                      ghost: {
                          type: WidgetGhostType.HISTOGRAM,
                          config: {
                              version: 1,
                              ...this.config
                          }
                      },
                      shell: {
                          type: WidgetShellType.CARD,
                          config: {
                              version: 1,
                              ...this.config
                          }
                      }
                  };
            this.updateWidgetSize();
        } else {
            if (changes.widget) {
                if (this._component) {
                    this.container.clear();
                    this._component = null;
                }
                const componentClass = WidgetClassStaticProvider.getWidgetFromLegacyWidgetCode(changes.widget.currentValue);
                if (!componentClass) return;

                this._component = this.container.createComponent(componentClass);

                this.updateWidgetInputs(changes);
            } else {
                this.updateWidgetInputs(changes);
            }
        }
    }

    updateWidgetInputs(changes: SimpleChanges) {
        if (changes.config) {
            this._component!.instance.config = changes.config.currentValue;
            this.updateWidgetSize();
        }
    }

    updateWidgetSize() {
        this.size =
            this.config && Object.values(WidgetSize).includes(this.config.size)
                ? this.config.size
                : this._component
                  ? this._component.instance.size
                  : WidgetSize.FULL;
        this.minWidth = this.width = this._sanitizer.bypassSecurityTrustStyle(this.getWidgetWidthFromSize(this.size));
    }

    getWidgetWidthFromSize(widgetSize: WidgetSize): string {
        switch (widgetSize) {
            case WidgetSize.FULL:
                return '100%';
            case WidgetSize.THREE_QUARTERS:
                return 'calc(100% * 3 / 4)';
            case WidgetSize.TWO_THIRDS:
                return 'calc(100% * 2 / 3)';
            case WidgetSize.HALF:
                return 'calc(100% / 2)';
            case WidgetSize.THIRD:
                return 'calc(100% / 3)';
            case WidgetSize.QUARTER:
                return 'calc(100% / 4)';
        }
    }
}
